import React, { useRef, useEffect } from 'react';
import Editor, { Monaco } from '@monaco-editor/react';
import { useTheme } from '../../contexts/ThemeContext';

interface ConditionEditorProps {
  value: string;
  onChange: (value: string) => void;
  onValidate?: (isValid: boolean, errors: string[]) => void;
  height?: string;
  readOnly?: boolean;
}

// Custom language definition for dialogue conditions
const CONDITION_LANGUAGE_ID = 'dialogue-condition';

const registerConditionLanguage = (monaco: Monaco) => {
  // Register the language if not already registered
  if (!monaco.languages.getLanguages().some(lang => lang.id === CONDITION_LANGUAGE_ID)) {
    monaco.languages.register({ id: CONDITION_LANGUAGE_ID });

    // Define tokens for syntax highlighting
    monaco.languages.setMonarchTokensProvider(CONDITION_LANGUAGE_ID, {
      keywords: ['AND', 'OR', 'NOT', 'true', 'false'],
      operators: ['==', '!=', '>', '<', '>=', '<=', '&&', '||', '!'],
      
      tokenizer: {
        root: [
          // Keywords
          [/\b(AND|OR|NOT|true|false)\b/, 'keyword'],
          
          // Flags and variables (e.g., flag.met_npc, player.level)
          [/\b(flag|player|npc|time|item|quest)\.[a-zA-Z_]\w*/, 'variable'],
          
          // Numbers
          [/\b\d+(\.\d+)?\b/, 'number'],
          
          // Strings
          [/"([^"\\]|\\.)*"/, 'string'],
          [/'([^'\\]|\\.)*'/, 'string'],
          
          // Operators
          [/(==|!=|>=|<=|>|<|&&|\|\||!)/, 'operator'],
          
          // Parentheses
          [/[()]/, 'delimiter.parenthesis'],
          
          // Comments
          [/\/\/.*$/, 'comment'],
        ],
      },
    });

    // Define theme colors
    monaco.editor.defineTheme('dialogue-light', {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '0000FF', fontStyle: 'bold' },
        { token: 'variable', foreground: '008080' },
        { token: 'number', foreground: '098658' },
        { token: 'string', foreground: 'A31515' },
        { token: 'operator', foreground: '000000' },
        { token: 'comment', foreground: '008000', fontStyle: 'italic' },
      ],
      colors: {},
    });

    monaco.editor.defineTheme('dialogue-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '569CD6', fontStyle: 'bold' },
        { token: 'variable', foreground: '4EC9B0' },
        { token: 'number', foreground: 'B5CEA8' },
        { token: 'string', foreground: 'CE9178' },
        { token: 'operator', foreground: 'D4D4D4' },
        { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
      ],
      colors: {},
    });
  }
};

// Common condition patterns for auto-completion
const getCompletionItems = (monaco: Monaco) => {
  return [
    // Flag completions
    {
      label: 'flag.',
      kind: monaco.languages.CompletionItemKind.Snippet,
      insertText: 'flag.${1:flag_name}',
      documentation: 'Check a game flag',
      range: undefined as any,
    },
    {
      label: 'NOT flag.',
      kind: monaco.languages.CompletionItemKind.Snippet,
      insertText: 'NOT flag.${1:flag_name}',
      documentation: 'Check if a flag is not set',
      range: undefined as any,
    },
    // Player completions
    {
      label: 'player.level',
      kind: monaco.languages.CompletionItemKind.Property,
      insertText: 'player.level',
      documentation: 'Player level',
      range: undefined as any,
    },
    {
      label: 'player.gold',
      kind: monaco.languages.CompletionItemKind.Property,
      insertText: 'player.gold',
      documentation: 'Player gold amount',
      range: undefined as any,
    },
    // Time completions
    {
      label: 'time.is_morning',
      kind: monaco.languages.CompletionItemKind.Property,
      insertText: 'time.is_morning',
      documentation: 'Check if it\'s morning',
      range: undefined as any,
    },
    {
      label: 'time.is_night',
      kind: monaco.languages.CompletionItemKind.Property,
      insertText: 'time.is_night',
      documentation: 'Check if it\'s night',
      range: undefined as any,
    },
    // NPC completions
    {
      label: 'npc.relationship',
      kind: monaco.languages.CompletionItemKind.Property,
      insertText: 'npc.relationship',
      documentation: 'NPC relationship value',
      range: undefined as any,
    },
  ];
};

export const ConditionEditor: React.FC<ConditionEditorProps> = ({
  value,
  onChange,
  onValidate,
  height = '100px',
  readOnly = false,
}) => {
  const { theme } = useTheme();
  const monacoRef = useRef<Monaco | null>(null);

  const handleEditorWillMount = (monaco: Monaco) => {
    registerConditionLanguage(monaco);
    
    // Register completion provider
    monaco.languages.registerCompletionItemProvider(CONDITION_LANGUAGE_ID, {
      provideCompletionItems: () => ({
        suggestions: getCompletionItems(monaco),
      }),
    });

    monacoRef.current = monaco;
  };

  const handleEditorChange = (value: string | undefined) => {
    const newValue = value || '';
    onChange(newValue);
    
    // Basic validation
    if (onValidate) {
      const errors: string[] = [];
      
      // Check for balanced parentheses
      let parenCount = 0;
      for (const char of newValue) {
        if (char === '(') parenCount++;
        if (char === ')') parenCount--;
        if (parenCount < 0) {
          errors.push('Unmatched closing parenthesis');
          break;
        }
      }
      if (parenCount > 0) {
        errors.push('Unmatched opening parenthesis');
      }
      
      // Check for empty operators
      if (/(\s(AND|OR|&&|\|\|)\s*$)|(^\s*(AND|OR|&&|\|\|)\s)/.test(newValue)) {
        errors.push('Incomplete logical expression');
      }
      
      onValidate(errors.length === 0, errors);
    }
  };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
      <Editor
        height={height}
        language={CONDITION_LANGUAGE_ID}
        theme={theme === 'dark' ? 'dialogue-dark' : 'dialogue-light'}
        value={value}
        onChange={handleEditorChange}
        beforeMount={handleEditorWillMount}
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'off',
          glyphMargin: false,
          folding: false,
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 0,
          renderLineHighlight: 'none',
          scrollBeyondLastLine: false,
          readOnly,
          suggestOnTriggerCharacters: true,
          quickSuggestions: true,
          wordWrap: 'on',
        }}
      />
    </div>
  );
};